import React from 'react';
import { format } from 'date-fns';
import { PrintModal } from './PrintModal';
import { PrintDocumentTitle, PrintInfoGrid } from '@/components/shared/print';
import { Badge } from '@/components/ui/badge';
import { CheckSquare } from 'lucide-react';

export interface PickingItem {
  id: string;
  category: string; // 'Vật tư in' | 'Vật tư phụ'
  materialCode: string;
  materialName: string;
  unit: string;
  plannedQty: number;
  issuedQty: number;
  pendingQty: number;
  locationCode?: string;
  lotNumber?: string;
  notes?: string;
}

export interface MaterialPickingListPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  poCode: string;
  productName: string;
  customerName?: string;
  createdDate?: Date | string;
  warehouseName?: string;
  items: PickingItem[];
}

export function MaterialPickingListPrintModal({
  isOpen,
  onClose,
  poCode,
  productName,
  customerName,
  createdDate = new Date(),
  warehouseName = 'Kho Vật Tư (KHO-VT)',
  items = [],
}: MaterialPickingListPrintModalProps) {
  const formattedDate = format(new Date(createdDate), 'dd/MM/yyyy HH:mm');
  const printDocNo = `PICK-${poCode}-${Date.now().toString().slice(-4)}`;

  const totalPlanned = items.reduce((s, i) => s + (Number(i.plannedQty) || 0), 0);
  const totalIssued = items.reduce((s, i) => s + (Number(i.issuedQty) || 0), 0);
  const totalPending = items.reduce((s, i) => s + (Number(i.pendingQty) || 0), 0);

  return (
    <PrintModal
      isOpen={isOpen}
      onClose={onClose}
      title="In Bảng Kê Nhặt Hàng NVL (Picking List)"
      documentTitle={`PickingList_${poCode}`}
      orientation="landscape"
    >
      <div className="bg-white p-6 text-black font-sans w-[280mm] min-h-[190mm] mx-auto print:p-2 print:max-w-none text-xs">
        {/* ISO 9001 Document Title */}
        <PrintDocumentTitle
          title="BẢNG KÊ NHẶT HÀNG NGUYÊN VẬT LIỆU (PICKING LIST)"
          subtitle="MATERIAL PICKING & STAGING SHEET • BM-KHO-02A"
          docNo={printDocNo}
          docNoLabel="Số Bảng Kê"
          date={formattedDate}
          dateLabel="Ngày Lập"
          extraMeta={<span className="italic text-slate-500">(Chỉ dẫn thủ kho và công nhân gom nhặt vật tư theo vị trí kệ trước khi cấp phát)</span>}
          className="my-3"
        />

        {/* Header Information Grid */}
        <div className="mb-4 border border-slate-300 rounded p-2.5 bg-slate-50/50">
          <PrintInfoGrid
            labelWidth="w-[140px]"
            leftColumn={[
              { label: 'Lệnh sản xuất (LSX)', value: poCode, isBold: true, highlight: true },
              { label: 'Tên sản phẩm', value: productName, isBold: true },
              { label: 'Khách hàng', value: customerName || 'Nội bộ' },
            ]}
            rightColumn={[
              { label: 'Kho xuất cấp', value: warehouseName, isBold: true },
              { label: 'Tổng số loại NVL', value: `${items.length} loại vật tư` },
              { label: 'Trạng thái cấp phát', value: totalPending === 0 ? 'Đã cấp đủ 100%' : `Còn thiếu: ${totalPending.toLocaleString('vi-VN')} đơn vị` },
            ]}
          />
        </div>

        {/* Picking Table */}
        <table className="w-full border-collapse border border-black mb-4">
          <thead>
            <tr className="bg-slate-100 text-slate-800 uppercase font-bold text-center">
              <th className="border border-black px-1.5 py-1.5 w-8">STT</th>
              <th className="border border-black px-2 py-1.5 text-left w-24">Phân Loại</th>
              <th className="border border-black px-2 py-1.5 text-left w-28">Mã Vật Tư</th>
              <th className="border border-black px-2 py-1.5 text-left">Tên Chi Tiết / Quy Cách</th>
              <th className="border border-black px-1.5 py-1.5 w-14">ĐVT</th>
              <th className="border border-black px-2 py-1.5 text-right w-24">SL Định Mức</th>
              <th className="border border-black px-2 py-1.5 text-right w-20">Đã Cấp</th>
              <th className="border border-black px-2 py-1.5 text-right w-24 text-indigo-900 bg-indigo-50/50">Cần Nhặt</th>
              <th className="border border-black px-2 py-1.5 w-24">Vị Trí Kệ</th>
              <th className="border border-black px-1.5 py-1.5 w-12 text-center">Đã Lấy</th>
              <th className="border border-black px-2 py-1.5 w-28">Ghi Chú</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black font-medium">
            {items.map((item, idx) => (
              <tr key={item.id || idx} className="text-center">
                <td className="border border-black px-1.5 py-1.5">{idx + 1}</td>
                <td className="border border-black px-2 py-1.5 text-left font-semibold text-slate-700">
                  {item.category}
                </td>
                <td className="border border-black px-2 py-1.5 text-left font-mono font-bold text-indigo-900 whitespace-nowrap">
                  {item.materialCode}
                </td>
                <td className="border border-black px-2 py-1.5 text-left font-semibold">
                  {item.materialName}
                </td>
                <td className="border border-black px-1.5 py-1.5 uppercase whitespace-nowrap">{item.unit}</td>
                <td className="border border-black px-2 py-1.5 text-right font-mono font-semibold">
                  {Number(item.plannedQty || 0).toLocaleString('vi-VN')}
                </td>
                <td className="border border-black px-2 py-1.5 text-right font-mono text-slate-600">
                  {Number(item.issuedQty || 0).toLocaleString('vi-VN')}
                </td>
                <td className="border border-black px-2 py-1.5 text-right font-mono font-bold text-indigo-700 bg-indigo-50/30 text-sm whitespace-nowrap">
                  {Number(item.pendingQty || 0).toLocaleString('vi-VN')}
                </td>
                <td className="border border-black px-2 py-1.5 text-slate-700 font-mono">
                  {item.locationCode || 'Mặc định'}
                </td>
                <td className="border border-black px-1.5 py-1.5 text-center">
                  <div className="w-4 h-4 border-2 border-black mx-auto rounded-xs" />
                </td>
                <td className="border border-black px-2 py-1.5 text-left text-slate-600 text-[10px]">
                  {item.notes || ''}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-100 font-bold">
              <td colSpan={5} className="border border-black px-2 py-1.5 text-right uppercase">
                Tổng Cộng:
              </td>
              <td className="border border-black px-2 py-1.5 text-right font-mono text-slate-900">
                {totalPlanned.toLocaleString('vi-VN')}
              </td>
              <td className="border border-black px-2 py-1.5 text-right font-mono text-slate-700">
                {totalIssued.toLocaleString('vi-VN')}
              </td>
              <td className="border border-black px-2 py-1.5 text-right font-mono text-indigo-900 text-sm">
                {totalPending.toLocaleString('vi-VN')}
              </td>
              <td colSpan={3} className="border border-black px-2 py-1.5"></td>
            </tr>
          </tbody>
        </table>

        {/* 4-Signature ISO Block */}
        <div className="grid grid-cols-4 gap-4 mt-6 print:break-inside-avoid text-center">
          <div>
            <div className="font-bold text-slate-800">Người Lập Bảng Kê</div>
            <div className="text-[10px] text-slate-500 mt-0.5">(Ký, ghi rõ họ tên)</div>
            <div className="h-16" />
          </div>
          <div>
            <div className="font-bold text-slate-800">Công Nhân Nhặt Hàng</div>
            <div className="text-[10px] text-slate-500 mt-0.5">(Ký, xác nhận vị trí kệ)</div>
            <div className="h-16" />
          </div>
          <div>
            <div className="font-bold text-slate-800">Thủ Kho Xuất Hàng</div>
            <div className="text-[10px] text-slate-500 mt-0.5">(Ký, đối chiếu tồn kho)</div>
            <div className="h-16" />
          </div>
          <div>
            <div className="font-bold text-slate-800">Người Nhận / Trưởng Ca</div>
            <div className="text-[10px] text-slate-500 mt-0.5">(Ký, nhận vật tư ra sàn)</div>
            <div className="h-16" />
          </div>
        </div>
      </div>
    </PrintModal>
  );
}
